import { SUPPORTED_EXCHANGES } from "../../constants/supported-cex";
import { DecimalSchema } from "../../decimal/decimal.schema";
import { PublicationStatusSchema } from "../solo-cms/publication-status";
import {
  ARENA_MATCH_OUTCOME,
  ARENA_MATCH_TIER,
  QUEST_APP,
  QUEST_FREQUENCY,
  QUEST_REWARD_DISTRIBUTION,
  QUEST_REWARD_TYPE,
  QUEST_SOCIAL,
  QUEST_TYPE,
  QUEST_WINDOW_ANCHOR,
  SPINNING_WHEEL_TYPE,
} from "./quest.enums";
import { Decimal } from "decimal.js";
import { z } from "zod";

/**
 * Longest a signup-anchored completion window may be authored as (30 days).
 *
 * Enforced here AND used as the client's countdown clamp, so the two cannot disagree — an
 * unbounded duration would render a deadline the countdown silently truncates.
 */
export const MAX_QUEST_WINDOW_HOURS = 30 * 24;

const TwitterCampaignInteractionData = z.object({
  campaignName: z.string().optional().nullable(),
  XAccountUsernameToFollow: z.string().optional().nullable(),
  XAccountIdToFollow: z.string().optional().nullable(),
  partnerName: z.string().optional().nullable(),
});

const SocialConnectionConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.SOCIAL_CONNECTION),
  data: z.object({
    connectSocial: z.enum(QUEST_SOCIAL),
  }),
});
export type SocialConnectionConfig = z.infer<typeof SocialConnectionConfigSchema>;

const TradingVolumeConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.TRADING_VOLUME),
  data: z.object({
    tradeVolume: z.number().positive(),
    cexs: z.array(z.enum(SUPPORTED_EXCHANGES)),
    marketIds: z.array(z.string().min(1)).optional(),
  }),
});
export type TradingVolumeConfig = z.infer<typeof TradingVolumeConfigSchema>;

const CexConnectionConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.CEX_CONNECTION),
  data: z.object({
    connectCex: z.nativeEnum(SUPPORTED_EXCHANGES),
  }),
});
export type CexConnectionConfig = z.infer<typeof CexConnectionConfigSchema>;

const TmaTonConnectionConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.TMA_TON_CONNECTION),
  data: z.object({}),
});
export type TmaTonConnectionConfig = z.infer<typeof TmaTonConnectionConfigSchema>;

const TmaFirstCexConnectionConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.TMA_FIRST_CEX_CONNECTION),
  data: z.object({}),
});
export type TmaFirstCexConnectionConfig = z.infer<typeof TmaFirstCexConnectionConfigSchema>;

const SpinningWheelConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.SPINNING_WHEEL),
  data: z.object({
    spinningWheelType: z.nativeEnum(SPINNING_WHEEL_TYPE),
    spinningWheelSlots: z.array(
      z.object({
        probability: z.number().nonnegative(),
        rewardCount: z.number().nonnegative(),
        rewardType: z.nativeEnum(QUEST_REWARD_TYPE),
      }),
    ),
  }),
});
export type SpinningWheelConfig = z.infer<typeof SpinningWheelConfigSchema>;

const UserAffiliatedConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.USER_AFFILIATED),
  data: z.object({
    connectCex: z.nativeEnum(SUPPORTED_EXCHANGES),
  }),
});
export type UserAffiliatedConfig = z.infer<typeof UserAffiliatedConfigSchema>;

const TwitterLikePostConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.TWITTER_LIKE_POST),
  data: z.object({
    postId: z.string().min(1),
  }),
});
export type TwitterLikePostConfig = z.infer<typeof TwitterLikePostConfigSchema>;

const TwitterRetweetPostConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.TWITTER_RETWEET_POST),
  data: z.object({
    postId: z.string().min(1),
  }),
});
export type TwitterRetweetPostConfig = z.infer<typeof TwitterRetweetPostConfigSchema>;

const TwitterFollowAccountConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.TWITTER_FOLLOW_ACCOUNT),
  data: z.object({
    accountId: z.string().min(1),
  }),
});
export type TwitterFollowAccountConfig = z.infer<typeof TwitterFollowAccountConfigSchema>;

const TwitterCommentPostConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.TWITTER_COMMENT_POST),
  data: z
    .object({
      XPostIdToComment: z.string().min(1),
    })
    .merge(TwitterCampaignInteractionData),
});
export type TwitterCommentPostConfig = z.infer<typeof TwitterCommentPostConfigSchema>;

const TwitterSendPostConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.TWITTER_SEND_POST),
  data: TwitterCampaignInteractionData,
});
export type TwitterSendPostConfig = z.infer<typeof TwitterSendPostConfigSchema>;

const KycAndTradingConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.KYC_AND_TRADING),
  data: z.object({
    connectCex: z.nativeEnum(SUPPORTED_EXCHANGES),
    tradeVolume: z.number().positive(),
  }),
});
export type KycAndTradingConfig = z.infer<typeof KycAndTradingConfigSchema>;

const ArenaMatchesConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.ARENA_MATCHES),
  data: z.object({
    matchCount: z.number().int().positive(),
    outcome: z.nativeEnum(ARENA_MATCH_OUTCOME),
    tier: z.nativeEnum(ARENA_MATCH_TIER),
    // Exact buy-ins a settled match must have been played at to count — a $10 match never
    // advances a $5 quest. Absent means any paid buy-in. Only meaningful on the paid tier.
    buyInsUsdt: z.array(z.number().positive()).min(1).optional(),
  }),
});
export type ArenaMatchesConfig = z.infer<typeof ArenaMatchesConfigSchema>;

/** Same USDT decimal-string rule the referral config uses, kept local to avoid a cycle. */
const BUY_IN_VOLUME_USDT_REGEX = /^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/;

/**
 * Cumulative settled buy-in spend, in USDT. Counts the same volume the referral module pays
 * on (paid classic 1v1 duels), so a referee's progress bar can never disagree with the
 * rewards their referrer sees.
 */
const ArenaBuyInVolumeConfigSchema = z.object({
  type: z.literal(QUEST_TYPE.ARENA_BUY_IN_VOLUME),
  data: z.object({
    /**
     * A decimal string, not a Decimal: this config is stored as JSON and crosses
     * the CMS boundary, where a Decimal instance would not survive the round trip.
     */
    // Strictly positive: progress divides by this target, so a 0 makes every credit
    // resolve to Infinity or NaN. Mirrors the positive-guarded `matchCount` sibling.
    // The regex is re-tested inside the refine because zod runs it even after the regex
    // check failed, and `new Decimal("abc")` throws instead of comparing false.
    buyInVolumeUsdt: z
      .string()
      .regex(BUY_IN_VOLUME_USDT_REGEX, { error: "must be a non-negative decimal with up to 6 decimals" })
      .refine((value) => BUY_IN_VOLUME_USDT_REGEX.test(value) && new Decimal(value).gt(0), {
        error: "must be greater than 0",
      }),
  }),
});
export type ArenaBuyInVolumeConfig = z.infer<typeof ArenaBuyInVolumeConfigSchema>;

export const QuestTypeConfigSchema = z.discriminatedUnion("type", [
  SocialConnectionConfigSchema,
  TradingVolumeConfigSchema,
  CexConnectionConfigSchema,
  TmaTonConnectionConfigSchema,
  TmaFirstCexConnectionConfigSchema,
  SpinningWheelConfigSchema,
  UserAffiliatedConfigSchema,
  TwitterLikePostConfigSchema,
  TwitterRetweetPostConfigSchema,
  TwitterFollowAccountConfigSchema,
  TwitterCommentPostConfigSchema,
  TwitterSendPostConfigSchema,
  KycAndTradingConfigSchema,
  ArenaMatchesConfigSchema,
  ArenaBuyInVolumeConfigSchema,
]);

export type QuestTypeConfig = z.infer<typeof QuestTypeConfigSchema>;

export const ArenaTicketExpirySchema = z.object({
  expiryValue: z.number().int().positive(),
  expiryUnit: z.enum(["hours", "days"]),
});
export type ArenaTicketExpiry = z.infer<typeof ArenaTicketExpirySchema>;

/**
 * A quest ticket pack is Cede spend that re-mints on every completion, so a CMS typo is a
 * recurring cost, not a one-off. Same bounds as the signup pack, which mints the same asset.
 */
export const QUEST_TICKET_PACK_MAX_LINES = 5;
export const QUEST_TICKET_PACK_MAX_ITEM_QUANTITY = 50;
export const QUEST_TICKET_PACK_MAX_TOTAL_VALUE_USDT = 1000;

/**
 * One line per denomination, and a pack worth under the ceiling (`sum(faceValue x quantity)`,
 * Decimal, never float). Two lines at the same face value also collide as React keys on the
 * quest card and disagree with the claim reveal, which collapses the pack into one total.
 */
const refineQuestTicketPack = (
  data: { tickets: { faceValueUsdt: Decimal; quantity: number }[] },
  ctx: z.RefinementCtx,
): void => {
  const seen = new Set<string>();
  for (const ticket of data.tickets) {
    const canonical = ticket.faceValueUsdt.toString();
    if (seen.has(canonical)) {
      ctx.addIssue({ code: "custom", path: ["tickets"], message: `duplicate face value ${canonical}` });
    }
    seen.add(canonical);
  }

  const totalValueUsdt = data.tickets.reduce(
    (total, ticket) => total.plus(ticket.faceValueUsdt.times(ticket.quantity)),
    new Decimal(0),
  );
  if (totalValueUsdt.gt(QUEST_TICKET_PACK_MAX_TOTAL_VALUE_USDT)) {
    ctx.addIssue({
      code: "custom",
      path: ["tickets"],
      message: `pack is worth ${totalValueUsdt.toString()} USDT, over the ${QUEST_TICKET_PACK_MAX_TOTAL_VALUE_USDT} USDT limit`,
    });
  }
};

/**
 * Arena entry tickets are minted per denomination, so one quest can pay out a mixed pack
 * (e.g. 2× $5 and 1× $10). `faceValueUsdt` must match an enabled ArenaTicketConfiguration.
 */
const ArenaTicketsRewardDataSchema = z.object({
  type: z.literal(QUEST_REWARD_TYPE.ARENA_TICKETS),
  data: z
    .object({
      tickets: z
        .array(
          z.object({
            faceValueUsdt: DecimalSchema,
            quantity: z.number().int().positive().max(QUEST_TICKET_PACK_MAX_ITEM_QUANTITY),
          }),
        )
        .min(1)
        .max(QUEST_TICKET_PACK_MAX_LINES),
    })
    .merge(ArenaTicketExpirySchema)
    .superRefine(refineQuestTicketPack),
});
export type ArenaTicketsRewardData = z.infer<typeof ArenaTicketsRewardDataSchema>;

/** Reward-type-specific payout shape. Absent for the flat-count reward types. */
export const QuestRewardDataSchema = z.discriminatedUnion("type", [ArenaTicketsRewardDataSchema]);
export type QuestRewardData = z.infer<typeof QuestRewardDataSchema>;

const QuestFieldsSchema = z.object({
  id: z.uuid().optional(),
  questId: z.number().int().positive(),
  name: z.string().min(1),
  description: z.string().min(1),
  app: z.nativeEnum(QUEST_APP),
  typeConfig: QuestTypeConfigSchema,
  frequency: z.nativeEnum(QUEST_FREQUENCY),
  rewardCount: DecimalSchema,
  rewardType: z.nativeEnum(QUEST_REWARD_TYPE),
  rewardData: QuestRewardDataSchema.optional(),
  rewardDistribution: z.nativeEnum(QUEST_REWARD_DISTRIBUTION),
  startDate: z.coerce.date(),
  endDate: z.coerce.date().optional(),
  windowAnchor: z.nativeEnum(QUEST_WINDOW_ANCHOR),
  windowDurationHours: z.number().int().positive().max(MAX_QUEST_WINDOW_HOURS).optional(),
  status: PublicationStatusSchema,
});

type QuestFields = z.infer<typeof QuestFieldsSchema>;

const refineQuestInvariants = (quest: QuestFields, ctx: z.RefinementCtx): void => {
  // rewardData is the payout spec, rewardType only its label — disagreement leaves the grant undefined.
  const needsRewardData = quest.rewardType === QUEST_REWARD_TYPE.ARENA_TICKETS;
  if (needsRewardData && quest.rewardData?.type !== quest.rewardType) {
    ctx.addIssue({ code: "custom", path: ["rewardData"], message: `rewardData.type must be "${quest.rewardType}"` });
  }
  if (!needsRewardData && quest.rewardData != null) {
    ctx.addIssue({
      code: "custom",
      path: ["rewardData"],
      message: `rewardType "${quest.rewardType}" does not take rewardData`,
    });
  }

  if (quest.typeConfig.type === QUEST_TYPE.ARENA_BUY_IN_VOLUME) {
    // Same reasoning as arena_matches below: the settlement worker only queries arena
    // quests, so one saved on `hub` would silently never progress.
    if (quest.app !== QUEST_APP.ARENA) {
      ctx.addIssue({
        code: "custom",
        path: ["app"],
        message: `quest type "${QUEST_TYPE.ARENA_BUY_IN_VOLUME}" requires app "${QUEST_APP.ARENA}"`,
      });
    }
    // The type is kept out of ARENA_QUEST_TYPES on purpose, so the board never lists it and
    // there is no surface to claim from: a claimable reward would complete and never pay.
    if (quest.rewardDistribution !== QUEST_REWARD_DISTRIBUTION.AUTO_GRANT) {
      ctx.addIssue({
        code: "custom",
        path: ["rewardDistribution"],
        message: `quest type "${QUEST_TYPE.ARENA_BUY_IN_VOLUME}" requires rewardDistribution "${QUEST_REWARD_DISTRIBUTION.AUTO_GRANT}"`,
      });
    }
  }

  if (quest.typeConfig.type === QUEST_TYPE.ARENA_MATCHES) {
    // An arena_matches quest saved on `hub` is a silent no-op: Arena filters the catalogue on
    // `app`, and the settlement worker only queries arena quests, so progress never fans out.
    if (quest.app !== QUEST_APP.ARENA) {
      ctx.addIssue({
        code: "custom",
        path: ["app"],
        message: `quest type "${QUEST_TYPE.ARENA_MATCHES}" requires app "${QUEST_APP.ARENA}"`,
      });
    }
    const { buyInsUsdt } = quest.typeConfig.data;
    // A free entry carries no fee, so any positive buy-in makes `tier: free` unsatisfiable and
    // silently drops every free entry under `tier: any`.
    if (buyInsUsdt != null && quest.typeConfig.data.tier !== ARENA_MATCH_TIER.PAID) {
      ctx.addIssue({
        code: "custom",
        path: ["typeConfig", "data", "buyInsUsdt"],
        message: `buyInsUsdt only applies to tier "${ARENA_MATCH_TIER.PAID}"`,
      });
    }
    // Matching is exact, so a repeat is a no-op the author almost certainly did not intend.
    if (buyInsUsdt != null && new Set(buyInsUsdt).size !== buyInsUsdt.length) {
      ctx.addIssue({
        code: "custom",
        path: ["typeConfig", "data", "buyInsUsdt"],
        message: "buyInsUsdt must not repeat a buy-in",
      });
    }
  }

  // A signup-anchored window is per-user, so it only makes sense on a quest each user completes once.
  if (quest.windowAnchor !== QUEST_WINDOW_ANCHOR.USER_SIGNUP) return;
  if (quest.frequency !== QUEST_FREQUENCY.ONCE) {
    ctx.addIssue({
      code: "custom",
      path: ["windowAnchor"],
      message: `windowAnchor "${QUEST_WINDOW_ANCHOR.USER_SIGNUP}" requires frequency "${QUEST_FREQUENCY.ONCE}"`,
    });
  }
  if (quest.windowDurationHours == null) {
    ctx.addIssue({
      code: "custom",
      path: ["windowDurationHours"],
      message: `windowDurationHours is required when windowAnchor is "${QUEST_WINDOW_ANCHOR.USER_SIGNUP}"`,
    });
  }
};

export const SaveQuestDtoSchema = QuestFieldsSchema.superRefine(refineQuestInvariants);
export type SaveQuestDto = z.infer<typeof SaveQuestDtoSchema>;

export const QuestDtoSchema = QuestFieldsSchema.extend({ id: z.uuid() }).superRefine(refineQuestInvariants);
export type QuestDto = z.infer<typeof QuestDtoSchema>;

export const QuestDtoArraySchema = z.array(QuestDtoSchema);
export type QuestDtoArray = z.infer<typeof QuestDtoArraySchema>;

export const GetQuestByPageQueryParamsSchema = z.object({
  page: z.coerce.number().optional(),
  size: z.coerce.number().optional(),
});

export type GetQuestByPageQueryParamsDto = z.infer<typeof GetQuestByPageQueryParamsSchema>;
